// Create / edit dialog for an athlete or coach profile (see
// `features/athletes.md`). Athletes and coaches get separate dialogs: the
// role is fixed by the `isCoach` prop (each Add button and each row opens the
// dialog for its own kind), never toggled inside the dialog. The coach dialog
// carries the description field; the athlete dialog does not. Both capture
// the full name, gender and date of birth (age is derived live, never typed)
// and up to ATHLETE_PHOTO_MAX profile photos. Photos upload immediately on
// pick (same mechanism as company logos) and their URLs are saved with the
// profile. The personal number is assigned by the server on creation and
// shown read-only when editing.
//
// Creation is a wizard: once the profile is saved, a second step offers to
// email a platform invitation right away (see `features/athlete-access.md`)
// instead of making staff reopen the profile page to send it. The date of
// birth just entered decides who is asked for: a minor's access goes to a
// parent or guardian, so the step defaults to the guardian's email; an adult
// is invited directly. The step can always be skipped — the profile keeps its
// access panel for later — and only appears for staff holding
// MANAGE_ATHLETE_ACCESS (inviting is a separate permission from managing
// profiles). Editing an existing profile stays single-step. The dialog goes
// full-screen on phone-sized viewports, so the same wizard serves the mobile
// admin surface and the desktop roster alike.

import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import MenuItem from '@mui/material/MenuItem';
import type { Athlete, AthleteAccessRelation, AthleteInput } from '../domain';
import {
  ATHLETE_ACCESS_RELATIONS,
  ATHLETE_NAME_MAX,
  ATHLETE_PHOTO_MAX,
  COACH_DESCRIPTION_MAX,
  GENDERS,
  athleteAge,
  isMinor,
  isValidEmail,
  validateAthlete,
} from '../domain';
import {
  createAthlete,
  inviteAthleteAccess,
  updateAthlete,
  uploadAthletePhoto,
} from '../data/apiClient';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';

interface AthleteDialogProps {
  open: boolean;
  /** The company an athlete created here belongs to. */
  companyId: number;
  /** The athlete being edited, or null to create a new one. */
  athlete: Athlete | null;
  /**
   * Which dialog this is: the coach dialog (with description) or the athlete
   * dialog. Fixed per opening — when editing, pass the profile's own role.
   */
  isCoach: boolean;
  onClose: () => void;
  onSaved: (athlete: Athlete) => void;
}

export function AthleteDialog({
  open,
  companyId,
  athlete,
  isCoach,
  onClose,
  onSaved,
}: AthleteDialogProps) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const theme = useTheme();
  // The wizard must work from a phone: full-screen below the sm breakpoint.
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [fullName, setFullName] = useState('');
  // '' until picked (creates start without a preselected gender).
  const [gender, setGender] = useState<string>('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [coachDescription, setCoachDescription] = useState('');
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Invite step (creation wizard only). `created` holds the profile the
  // server just made and doubles as the step marker: null = profile form,
  // set = invite step.
  const [created, setCreated] = useState<Athlete | null>(null);
  const [email, setEmail] = useState('');
  const [relation, setRelation] = useState<AthleteAccessRelation>('SELF');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [inviteSent, setInviteSent] = useState(false);

  // Inviting is a separate act from creating the profile; without the
  // permission the dialog stays single-step, exactly as before.
  const wizard = athlete === null && hasPermission('MANAGE_ATHLETE_ACCESS');

  // (Re)seed the form whenever the dialog opens for a different target.
  useEffect(() => {
    if (!open) return;
    setFullName(athlete?.fullName ?? '');
    setGender(athlete?.gender ?? '');
    setDateOfBirth(athlete?.dateOfBirth ?? '');
    setCoachDescription(athlete?.coachDescription ?? '');
    setPhotoUrls(athlete?.photoUrls ?? []);
    setError(null);
    setCreated(null);
    setEmail('');
    setEmailError(null);
    setInviteSent(false);
  }, [open, athlete]);

  // Live field validation drives the inline errors and the Save button; the
  // server re-validates with the same shared domain rules regardless.
  const validation = useMemo(
    () =>
      validateAthlete({
        fullName,
        gender,
        dateOfBirth,
        isCoach,
        coachDescription,
        photoUrls,
      }),
    [fullName, gender, dateOfBirth, isCoach, coachDescription, photoUrls],
  );
  const age = useMemo(() => athleteAge(dateOfBirth), [dateOfBirth]);

  const pickPhoto = () => fileInputRef.current?.click();

  const onPhotoPicked = async (file: File | null) => {
    if (!file || photoUrls.length >= ATHLETE_PHOTO_MAX) return;
    setUploading(true);
    setError(null);
    try {
      const url = await uploadAthletePhoto(file);
      setPhotoUrls((current) =>
        current.length < ATHLETE_PHOTO_MAX ? [...current, url] : current,
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.athletes.errors.upload'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const submit = async () => {
    if (!validation.valid || busy) return;
    const input: AthleteInput = {
      fullName,
      gender,
      dateOfBirth,
      isCoach,
      coachDescription,
      photoUrls,
    };
    setBusy(true);
    setError(null);
    try {
      const saved = athlete
        ? await updateAthlete(athlete.personalNumber, input)
        : await createAthlete(companyId, input);
      onSaved(saved);
      if (wizard) {
        // The profile exists now (the roster behind us already reloaded);
        // move on to the invitation. A minor's access goes to a parent or
        // guardian, so their date of birth picks the default relation.
        setRelation(isMinor(saved.dateOfBirth) ? 'GUARDIAN' : 'SELF');
        setCreated(saved);
      } else {
        onClose();
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.athletes.errors.save'));
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async () => {
    if (!created || busy) return;
    if (!isValidEmail(email)) {
      setEmailError(t('admin.athleteAccess.invite.emailInvalid'));
      return;
    }
    setBusy(true);
    setError(null);
    setEmailError(null);
    try {
      await inviteAthleteAccess(created.personalNumber, email.trim(), relation);
      setInviteSent(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.athleteAccess.invite.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
    >
      <DialogTitle>
        {created
          ? t('admin.athleteAccess.invite.title')
          : athlete
            ? t(
                isCoach
                  ? 'admin.athletes.dialog.editCoachTitle'
                  : 'admin.athletes.dialog.editTitle',
                { name: athlete.fullName },
              )
            : t(
                isCoach
                  ? 'admin.athletes.dialog.createCoachTitle'
                  : 'admin.athletes.dialog.createTitle',
              )}
      </DialogTitle>
      <DialogContent>
        {wizard && (
          <Stepper activeStep={created ? 1 : 0} sx={{ mt: 1, mb: 2 }}>
            <Step>
              <StepLabel>{t('admin.athletes.wizard.stepProfile')}</StepLabel>
            </Step>
            <Step>
              <StepLabel>{t('admin.athletes.wizard.stepInvite')}</StepLabel>
            </Step>
          </Stepper>
        )}
        {created ? (
          // Invite step: the profile is saved; offer to email the sign-in
          // invitation now. Skipping is always fine — the profile page's
          // access panel covers it later.
          <Stack spacing={2} sx={{ mt: wizard ? 0 : 1 }}>
            <Alert severity="success">
              {t('admin.athletes.wizard.created', {
                name: created.fullName,
                number: created.personalNumber,
              })}
            </Alert>
            {inviteSent ? (
              <Alert severity="success">
                {t('admin.athleteAccess.invite.sent', { email: email.trim() })}
              </Alert>
            ) : (
              <>
                {error && <Alert severity="error">{error}</Alert>}
                <DialogContentText>
                  {t('admin.athleteAccess.invite.body', { name: created.fullName })}
                </DialogContentText>
                {isMinor(created.dateOfBirth) && (
                  <Alert severity="info">{t('admin.athleteAccess.hintMinor')}</Alert>
                )}
                <TextField
                  select
                  fullWidth
                  label={t('admin.athleteAccess.invite.relation')}
                  value={relation}
                  onChange={(e) =>
                    setRelation(e.target.value as AthleteAccessRelation)
                  }
                  helperText={t(`admin.athleteAccess.relationHelp.${relation}`)}
                >
                  {ATHLETE_ACCESS_RELATIONS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {t(`admin.athleteAccess.relation.${value}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  autoFocus
                  type="email"
                  label={t('admin.athleteAccess.invite.email')}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(null);
                  }}
                  error={emailError !== null}
                  helperText={emailError ?? t('admin.athleteAccess.invite.emailHelp')}
                />
              </>
            )}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: wizard ? 0 : 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {athlete ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.athletes.dialog.personalNumber')}
                </Typography>
                <Chip
                  size="small"
                  label={athlete.personalNumber}
                  sx={{ fontFamily: 'monospace' }}
                />
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('admin.athletes.dialog.numberAssigned')}
              </Typography>
            )}

            <TextField
              label={t('admin.athletes.dialog.fullName')}
              fullWidth
              autoFocus
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              slotProps={{ htmlInput: { maxLength: ATHLETE_NAME_MAX } }}
            />
            <TextField
              select
              label={t('admin.athletes.dialog.gender')}
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              sx={{ maxWidth: 260 }}
            >
              {GENDERS.map((code) => (
                <MenuItem key={code} value={code}>
                  {t(`admin.athletes.gender.${code}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('admin.athletes.dialog.dateOfBirth')}
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              error={dateOfBirth.length > 0 && validation.errors.dateOfBirth !== undefined}
              helperText={
                age !== null
                  ? t('admin.athletes.dialog.age', { count: age })
                  : t('admin.athletes.dialog.ageHelp')
              }
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ maxWidth: 260 }}
            />

            {isCoach && (
              <TextField
                label={t('admin.athletes.dialog.coachDescription')}
                fullWidth
                multiline
                minRows={3}
                value={coachDescription}
                onChange={(e) => setCoachDescription(e.target.value)}
                helperText={t('admin.athletes.dialog.coachDescriptionHelp', {
                  max: COACH_DESCRIPTION_MAX,
                })}
                slotProps={{ htmlInput: { maxLength: COACH_DESCRIPTION_MAX } }}
              />
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('admin.athletes.dialog.photos', {
                  count: photoUrls.length,
                  max: ATHLETE_PHOTO_MAX,
                })}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {photoUrls.map((url) => (
                  <Box
                    key={url}
                    sx={{
                      position: 'relative',
                      width: 96,
                      height: 96,
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt={t('admin.athletes.dialog.photoAlt', { name: fullName })}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      aria-label={t('admin.athletes.dialog.removePhoto')}
                      disabled={busy}
                      onClick={() =>
                        setPhotoUrls((current) => current.filter((u) => u !== url))
                      }
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        bgcolor: 'rgba(0,0,0,0.55)',
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {photoUrls.length < ATHLETE_PHOTO_MAX && (
                  <Button
                    variant="outlined"
                    disabled={busy || uploading}
                    onClick={pickPhoto}
                    sx={{ width: 96, height: 96, flexDirection: 'column', gap: 0.5 }}
                  >
                    {uploading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <AddPhotoAlternateIcon />
                    )}
                    <Typography variant="caption">
                      {t('admin.athletes.dialog.addPhoto')}
                    </Typography>
                  </Button>
                )}
              </Stack>
              {/* MUI has no file-picker component; the input is visually hidden
                  and driven by the button above. */}
              <Box
                component="input"
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  void onPhotoPicked(e.target.files?.[0] ?? null)
                }
                sx={{ display: 'none' }}
              />
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {created ? (
          inviteSent ? (
            <Button variant="contained" onClick={onClose}>
              {t('admin.common.done')}
            </Button>
          ) : (
            <>
              {/* Closing here is the skip: the profile is already saved. */}
              <Button disabled={busy} onClick={onClose}>
                {t('admin.athletes.wizard.skip')}
              </Button>
              <Button
                variant="contained"
                disabled={busy}
                onClick={() => void sendInvite()}
              >
                {t('admin.athleteAccess.invite.send')}
              </Button>
            </>
          )
        ) : (
          <>
            <Button disabled={busy} onClick={onClose}>
              {t('admin.common.cancel')}
            </Button>
            <Button
              variant="contained"
              disabled={busy || uploading || !validation.valid}
              onClick={() => void submit()}
            >
              {t(athlete ? 'admin.common.save' : 'admin.athletes.wizard.createAction')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
